/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import React from "react";
import NavLinkAdapter from "../../../NavLinkAdapter";
import { alpha, styled } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import clsx from "clsx";
import PropTypes from "prop-types";
import { useMemo } from "react";
import BASENavBadge from "../../BASENavBadge";
import BASESvgIcon from "../../../BASESvgIcon";

const Root = styled(ListItem)(({ theme, ...props }) => ({
  minHeight: 44,
  width: "100%",
  borderRadius: "6px",
  margin: "0 0 4px 0",
  paddingRight: 16,
  paddingLeft: props.itempadding > 80 ? 80 : props.itempadding,
  paddingTop: 10,
  paddingBottom: 10,
  color: alpha(theme.palette.text.primary, 0.7),
  cursor: "pointer",
  textDecoration: "none!important",
  "&:hover": {
    color: theme.palette.text.primary,
  },
  "&.active": {
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0, 0, 0, .05)!important"
        : "rgba(255, 255, 255, .1)!important",
    pointerEvents: "none",
    transition: "border-radius .15s cubic-bezier(0.4,0.0,0.2,1)",
    "& > .base-list-item-text-primary": {
      color: "inherit",
    },
    "& > .base-list-item-icon": {
      color: "inherit",
    },
  },
  "& >.base-list-item-icon": {
    marginRight: 16,
    color: "inherit",
  },
  "& > .base-list-item-text": {},
}));

function BASENavVerticalItem(props) {
  const { item, nestedLevel, onItemClick } = props;

  const itempadding = nestedLevel > 0 ? 38 + nestedLevel * 16 : 16;

  return useMemo(
    () => (
      <Root
        button
        component={NavLinkAdapter}
        to={item.url || ""}
        activeClassName={item.url ? "active" : ""}
        className={clsx("base-list-item", item.active && "active")}
        onClick={() => onItemClick && onItemClick(item)}
        end={item.end}
        itempadding={itempadding}
        role="button"
        sx={item.sx}
        disabled={item.disabled}
      >
        {item.icon && (
          <BASESvgIcon
            className={clsx("base-list-item-icon shrink-0", item.iconClass)}
            color="action"
          >
            {item.icon}
          </BASESvgIcon>
        )}

        <ListItemText
          className="base-list-item-text"
          primary={item.title}
          secondary={item.subtitle}
          classes={{
            primary: "text-13 font-medium base-list-item-text-primary truncate",
            secondary:
              "text-11 font-medium base-list-item-text-secondary leading-normal truncate",
          }}
        />
        {item.badge && <BASENavBadge badge={item.badge} />}
      </Root>
    ),
    [item, itempadding, onItemClick]
  );
}

BASENavVerticalItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    icon: PropTypes.string,
    url: PropTypes.string,
  }),
};

// BASENavVerticalItem.defaultProps = {};

const NavVerticalItem = BASENavVerticalItem;

export default NavVerticalItem;
